import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./CustomerSupport.css";
import API_BASE_URL from "../RestAPI";
import {
    customerSupportApi,
    PRIORITY_CHIP_CLS,
    PRIORITY_DOT,
    STATUS_CHIP_CLS,
    STATUS_DOT,
    STATUS_VALUES,
    PRIORITY_VALUES,
    FIXDESK_TYPE_VALUES,
    SEVERITY_VALUES,
    SEVERITY_HELP,
    SEVERITY_SHORT,
    initials,
    avatarGradient,
    relativeTime,
} from "./customerSupportApi";

/* Load the company's employee list — same endpoint the rest of the app uses. */
const fetchEmployees = async () => {
    const token = localStorage.getItem("authToken");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API_BASE_URL.replace(/\/$/, "")}/core/employee/`, { headers });
    const data = await res.json().catch(() => ({}));
    if (data?.status === "success") return data.data || [];
    return [];
};

const showToast = (msg) => {
    let t = document.getElementById("cs-toast");
    if (!t) {
        t = document.createElement("div");
        t.id = "cs-toast";
        t.className = "cs-toast";
        document.body.appendChild(t);
    }
    t.textContent = msg;
    requestAnimationFrame(() => t.classList.add("show"));
    clearTimeout(t._hide);
    t._hide = setTimeout(() => t.classList.remove("show"), 2000);
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
    });
};

const TicketDetailModal = ({ ticket, open, onClose, onSaved }) => {
    /* Editable state — kept in sync with whatever ticket was passed in. */
    const [status, setStatus] = useState(ticket?.status || "Open");
    const [priority, setPriority] = useState(ticket?.priority || "Medium");
    const [assignTo, setAssignTo] = useState(ticket?.assigned_to ? String(ticket.assigned_to) : "");
    const [note, setNote] = useState(ticket?.fixdesk_internal_note || "");
    const [dueDate, setDueDate] = useState(ticket?.due_date || "");
    const [severity, setSeverity] = useState(ticket?.severity || "");
    const [fixdeskType, setFixdeskType] = useState(ticket?.fixdesk_type || "");
    const [moved, setMoved] = useState(!!ticket?.fixdesk_moved_to_requirements);
    const [hotfix, setHotfix] = useState(!!ticket?.fixdesk_hotfix_applied);
    const [sprint, setSprint] = useState(!!ticket?.fixdesk_moved_to_sprint);

    /* Full detail (description + comments + attachments) is fetched on open. */
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [employees, setEmployees] = useState([]);
    const [empLoading, setEmpLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    /* Reply composer */
    const [replyText, setReplyText] = useState("");
    const [posting, setPosting] = useState(false);

    const overlayRef = useRef(null);
    const timelineRef = useRef(null);

    /* Reset whenever a new ticket is opened */
    useEffect(() => {
        if (!ticket) return;
        setStatus(ticket.status || "Open");
        setPriority(ticket.priority || "Medium");
        setAssignTo(ticket.assigned_to ? String(ticket.assigned_to) : "");
        setNote(ticket.fixdesk_internal_note || "");
        setDueDate(ticket.due_date || "");
        setSeverity(ticket.severity || "");
        setFixdeskType(ticket.fixdesk_type || "");
        setMoved(!!ticket.fixdesk_moved_to_requirements);
        setHotfix(!!ticket.fixdesk_hotfix_applied);
        setSprint(!!ticket.fixdesk_moved_to_sprint);
        setDetail(null);
        setReplyText("");
    }, [ticket]);

    /* Load full ticket detail + employees when the modal opens */
    useEffect(() => {
        if (!open || !ticket) return;
        let cancelled = false;

        setDetailLoading(true);
        customerSupportApi.getTicket(ticket.id)
            .then((res) => { if (!cancelled) setDetail(res.data || null); })
            .catch(() => { /* fall back to whatever the row had */ })
            .finally(() => { if (!cancelled) setDetailLoading(false); });

        setEmpLoading(true);
        fetchEmployees()
            .then((rows) => { if (!cancelled) setEmployees(rows); })
            .finally(() => { if (!cancelled) setEmpLoading(false); });

        return () => { cancelled = true; };
    }, [open, ticket]);

    /* Lock body scroll + Esc to close */
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const handler = (e) => {
            if (e.key === "Escape") { e.preventDefault(); handleClose(); }
            else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                // If focus is on the reply composer, let its own handler send
                // the reply instead of saving the triage form.
                const active = document.activeElement;
                if (active && active.dataset && active.dataset.replyComposer) return;
                e.preventDefault(); handleSave();
            }
        };
        window.addEventListener("keydown", handler);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, status, priority, assignTo, note]);

    const dirty = useMemo(() => {
        if (!ticket) return false;
        const curAssign = ticket.assigned_to ? String(ticket.assigned_to) : "";
        return (
            status !== ticket.status ||
            priority !== ticket.priority ||
            assignTo !== curAssign ||
            note !== (ticket.fixdesk_internal_note || "") ||
            (dueDate || "") !== (ticket.due_date || "") ||
            (fixdeskType || "") !== (ticket.fixdesk_type || "") ||
            (severity || "") !== (ticket.severity || "") ||
            moved !== !!ticket.fixdesk_moved_to_requirements ||
            hotfix !== !!ticket.fixdesk_hotfix_applied ||
            sprint !== !!ticket.fixdesk_moved_to_sprint
        );
    }, [ticket, status, priority, assignTo, note, dueDate, fixdeskType, severity, moved, hotfix, sprint]);

    const handleClose = () => {
        if (saving) return;
        onClose && onClose();
    };

    const handleSave = async () => {
        if (!ticket || saving || !dirty) return;
        setSaving(true);
        try {
            const emp = employees.find((e) => String(e.id) === assignTo);
            const payload = {
                status,
                priority,
                assigned_to_id: assignTo ? Number(assignTo) : null,
                assigned_to_name: emp ? emp.name : (assignTo ? ticket.assigned_to_name : ""),
                fixdesk_internal_note: note,
                due_date: dueDate || null,
                fixdesk_type: fixdeskType || "",
                // FixDesk-only tier. Changing it re-stamps the ticket's
                // deadlines server-side from the policy for the new severity.
                severity: severity || "",
                fixdesk_moved_to_requirements: moved,
                fixdesk_hotfix_applied: hotfix,
                fixdesk_moved_to_sprint: sprint,
            };
            const res = await customerSupportApi.updateTriage(ticket.id, payload);
            showToast("Ticket updated");
            if (onSaved) onSaved(res.data || null);
            onClose && onClose();
        } catch (e) {
            showToast(e.message || "Failed to update ticket");
        } finally {
            setSaving(false);
        }
    };

    const handlePostReply = async () => {
        const msg = replyText.trim();
        if (!msg || posting || !ticket) return;
        setPosting(true);
        try {
            const res = await customerSupportApi.addComment(ticket.id, { message: msg });
            const newComment = res?.data;
            // Append to the locally-cached detail so the timeline shows it instantly.
            setDetail((prev) => {
                const current = prev || {};
                return {
                    ...current,
                    comments: [...(current.comments || []), newComment].filter(Boolean),
                };
            });
            setReplyText("");
            // Scroll the timeline to the bottom so the new bubble is visible.
            setTimeout(() => {
                if (timelineRef.current) {
                    timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
                }
            }, 30);
            showToast("Reply sent");
        } catch (e) {
            showToast(e.message || "Failed to send reply");
        } finally {
            setPosting(false);
        }
    };

    if (!open || !ticket) return null;

    const desc = detail?.description ?? "";
    const comments = detail?.comments || [];
    const attachments = detail?.attachments || [];

    const modal = (
        <div
            ref={overlayRef}
            className="nt-overlay"
            onMouseDown={(e) => { if (e.target === overlayRef.current) handleClose(); }}
        >
            <div className="nt-modal nt-modal--wide" role="dialog" aria-modal="true" aria-label="Ticket detail">
                {/* Header */}
                <div className="nt-hdr">
                    <div className="nt-icon" style={{ background: "linear-gradient(135deg,#3b82f6,#7c5cff)" }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                        </svg>
                    </div>
                    <div className="nt-title-wrap">
                        <div className="nt-title" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span style={{ fontFamily: "var(--cs-mono)", fontSize: 12, color: "var(--cs-accent-l)" }}>
                                {ticket.ticket_code}
                            </span>
                            <span>{ticket.subject}</span>
                        </div>
                        <div className="nt-sub">
                            {ticket.customer_name} · {ticket.type} · {ticket.source}
                            {ticket.source_created_at ? <> · opened {relativeTime(ticket.source_created_at)}</> : null}
                        </div>
                    </div>
                    <button className="nt-close" onClick={handleClose} aria-label="Close">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Body — two-pane layout: left edit pane, right timeline rail */}
                <div className="nt-detail-body">
                    {/* Summary bar — spans both columns */}
                    <div className="nt-detail-summary">
                        {ticket.severity && (
                            <span
                                className={`sev-chip sev-${ticket.severity.toLowerCase()}`}
                                title={SEVERITY_HELP[ticket.severity]}
                            >
                                {ticket.severity} · {SEVERITY_SHORT[ticket.severity]}
                            </span>
                        )}
                        <span className={`pr-chip ${PRIORITY_CHIP_CLS[ticket.priority]}`}>
                            <span className="chip-dot" style={{ background: PRIORITY_DOT[ticket.priority] }}></span>
                            {ticket.priority}
                        </span>
                        <span className={`chip ${STATUS_CHIP_CLS[ticket.status]}`}>
                            <span className="chip-dot" style={{ background: STATUS_DOT[ticket.status] }}></span>
                            {ticket.status}
                        </span>
                        {ticket.category && (
                            <span className="chip" style={{ background: "var(--cs-accent-soft)", color: "var(--cs-accent-l)" }}>
                                {ticket.category}{ticket.subcategory ? ` · ${ticket.subcategory}` : ""}
                            </span>
                        )}
                        <span className={`sla-pill sla-${ticket.sla_status || "ok"}`} style={{ marginLeft: "auto" }}>
                            SLA · {ticket.sla_status === "danger" ? "Breached" : ticket.sla_status === "warn" ? "At Risk" : "On Track"}
                        </span>
                    </div>

                    <div className="nt-detail-grid">
                        {/* ── LEFT pane: ticket info + edit controls ── */}
                        <div className="nt-detail-left">
                            {/* Meta grid */}
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, 1fr)",
                                gap: 12,
                                marginBottom: 18,
                                padding: 14,
                                background: "var(--cs-surface-2)",
                                borderRadius: 10,
                                border: "1px solid var(--cs-border)",
                            }}>
                                <MetaRow label="Customer" value={ticket.customer_name} />
                                <MetaRow label="Clinic / Branch" value={ticket.clinic_name || ticket.customer_dept || "—"} />
                                <MetaRow label="Submitted by" value={detail?.submitted_by_name || "—"} />
                                <MetaRow label="Module" value={detail?.module || ticket.source || "—"} />
                                <MetaRow label="Created" value={fmtDate(ticket.source_created_at)} />
                                <MetaRow label="Last activity" value={fmtDate(ticket.source_last_activity_at)} />
                                {ticket.source_resolved_at && (
                                    <MetaRow label="Resolved" value={fmtDate(ticket.source_resolved_at)} />
                                )}
                            </div>

                            {/* Page location — where the issue was raised inside eMedHub. */}
                            {(detail?.screen_path || detail?.module || detail?.version || detail?.browser) && (
                                <div className="nt-field">
                                    <div className="nt-label">
                                        Page Location
                                        <span className="hint">captured at submission</span>
                                    </div>
                                    <div style={{
                                        fontSize: 12.5,
                                        color: "var(--cs-text-sec)",
                                        background: "var(--cs-bg)",
                                        border: "1px solid var(--cs-border)",
                                        borderRadius: 8,
                                        padding: "10px 12px",
                                        fontFamily: "var(--cs-mono)",
                                        wordBreak: "break-all",
                                    }}>
                                        {detail?.screen_path
                                            ? <div><span style={{ color: "var(--cs-text-dim)" }}>route: </span>{detail.screen_path}</div>
                                            : null}
                                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontFamily: "var(--cs-font)", marginTop: detail?.screen_path ? 6 : 0 }}>
                                            {detail?.module   && <span><span style={{ color: "var(--cs-text-dim)" }}>module: </span>{detail.module}</span>}
                                            {detail?.version  && <span><span style={{ color: "var(--cs-text-dim)" }}>version: </span>{detail.version}</span>}
                                            {detail?.browser  && <span><span style={{ color: "var(--cs-text-dim)" }}>browser: </span>{detail.browser}</span>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Reopen badge if the ticket has bounced back at least once. */}
                            {(ticket.reopen_count || 0) > 0 && (
                                <div style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "4px 10px",
                                    borderRadius: 12,
                                    background: "rgba(249, 115, 22, .14)",
                                    border: "1px solid rgba(249, 115, 22, .35)",
                                    color: "#fb923c",
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    marginBottom: 14,
                                }}>
                                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <polyline points="23 4 23 10 17 10" />
                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
                                    </svg>
                                    Reopened {ticket.reopen_count} time{ticket.reopen_count === 1 ? "" : "s"}
                                </div>
                            )}

                            {/* Description */}
                            <div className="nt-field">
                                <div className="nt-label">Description</div>
                                {detailLoading && !desc ? (
                                    <div style={{ color: "var(--cs-text-dim)", fontSize: 12 }}>Loading…</div>
                                ) : (
                                    <div style={{
                                        whiteSpace: "pre-wrap",
                                        fontSize: 13,
                                        lineHeight: 1.55,
                                        color: "var(--cs-text-sec)",
                                        padding: 12,
                                        background: "var(--cs-bg)",
                                        borderRadius: 8,
                                        border: "1px solid var(--cs-border)",
                                        maxHeight: 220,
                                        overflowY: "auto",
                                    }}>
                                        {desc || <span style={{ color: "var(--cs-text-dim)" }}>(no description)</span>}
                                    </div>
                                )}
                            </div>

                            {/* Severity — the tier the turnaround target runs
                                against. Buttons rather than a <select> because
                                there are only four and the choice deserves to
                                be visible without opening anything: an
                                untriaged ticket has no clock on it at all. */}
                            <div className="nt-field">
                                <div className="nt-label">
                                    Severity
                                    <span className="hint">
                                        FixDesk-only · sets the turnaround target
                                        {ticket.severity_source === "auto" && severity === ticket.severity
                                            ? " · currently seeded from the customer's priority"
                                            : ""}
                                    </span>
                                </div>
                                <div className="sev-picker">
                                    {SEVERITY_VALUES.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            className={`sev-opt sev-opt-${s.toLowerCase()} ${severity === s ? "on" : ""}`}
                                            onClick={() => setSeverity(severity === s ? "" : s)}
                                            disabled={saving}
                                            title={SEVERITY_HELP[s]}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                                {/* The definition, always visible for whichever tier
                                    is selected. A tier nobody can define gets used
                                    wrongly within a fortnight. */}
                                <div className="sev-help">
                                    {severity
                                        ? SEVERITY_HELP[severity]
                                        : "No severity set — no turnaround target applies to this ticket, and it is invisible to every SLA number on the dashboard."}
                                </div>
                            </div>

                            {/* Turnaround — the three clocks. The active number is
                                what support actually spent; the other two are the
                                context that stops it being argued with. */}
                            {(ticket.active_tat_display || ticket.tat_display) && (
                                <div className="nt-field">
                                    <div className="nt-label">
                                        Turnaround
                                        <span className="hint">
                                            {ticket.tat_resolved ? "final" : "still running"}
                                        </span>
                                    </div>
                                    <div className="tat-panel">
                                        <div className="tat-line primary">
                                            <span className="tat-line-k">
                                                Support time
                                                <span className="tat-line-sub">
                                                    Working hours only, time waiting on the customer removed
                                                </span>
                                            </span>
                                            <span className={`tat-line-v ${
                                                ticket.resolution_breached ? "over"
                                                    : (ticket.sla_target && ticket.tat_resolved ? "under" : "")
                                            }`}>
                                                {ticket.active_tat_display || "—"}
                                                {ticket.sla_target && (
                                                    <small> / {ticket.sla_target.resolution_display}</small>
                                                )}
                                            </span>
                                        </div>

                                        <div className="tat-line">
                                            <span className="tat-line-k">
                                                First response
                                                <span className="tat-line-sub">
                                                    Until the first reply from an agent
                                                </span>
                                            </span>
                                            <span className={`tat-line-v ${ticket.frt_breached ? "over" : ""}`}>
                                                {ticket.frt_display || "No reply yet"}
                                                {ticket.sla_target && (
                                                    <small> / {ticket.sla_target.first_response_display}</small>
                                                )}
                                            </span>
                                        </div>

                                        <div className="tat-line">
                                            <span className="tat-line-k">
                                                Elapsed overall
                                                <span className="tat-line-sub">
                                                    Wall clock — what the customer experienced
                                                </span>
                                            </span>
                                            <span className="tat-line-v">{ticket.tat_display || "—"}</span>
                                        </div>

                                        {ticket.waiting_display && (
                                            <div className="tat-line">
                                                <span className="tat-line-k">
                                                    Waiting on customer
                                                    <span className="tat-line-sub">
                                                        Between an agent reply and the customer's next message
                                                    </span>
                                                </span>
                                                <span className="tat-line-v">{ticket.waiting_display}</span>
                                            </div>
                                        )}

                                        {ticket.resolution_due_at && !ticket.tat_resolved && (
                                            <div className="tat-line">
                                                <span className="tat-line-k">Due by</span>
                                                <span className={`tat-line-v ${ticket.resolution_breached ? "over" : ""}`}>
                                                    {fmtDate(ticket.resolution_due_at)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Status + Priority */}
                            <div className="nt-row">
                                <div className="nt-field">
                                    <div className="nt-label">Status</div>
                                    <select className="nt-select" value={status} onChange={(e) => setStatus(e.target.value)} disabled={saving}>
                                        {STATUS_VALUES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="nt-field">
                                    <div className="nt-label">
                                        Priority
                                        <span className="hint">customer-set · writes back to eMedHub</span>
                                    </div>
                                    <select className="nt-select" value={priority} onChange={(e) => setPriority(e.target.value)} disabled={saving}>
                                        {PRIORITY_VALUES.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Assign To + Due Date */}
                            <div className="nt-row">
                                <div className="nt-field">
                                    <div className="nt-label">
                                        Assign To
                                        {empLoading && <span className="hint">loading…</span>}
                                    </div>
                                    <select className="nt-select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} disabled={saving}>
                                        <option value="">Unassigned</option>
                                        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                                    </select>
                                </div>
                                <div className="nt-field">
                                    <div className="nt-label">
                                        Due Date
                                        <span className="hint">FixDesk-only · clears on blank</span>
                                    </div>
                                    <input
                                        type="date"
                                        className="nt-input"
                                        value={dueDate}
                                        onChange={(e) => setDueDate(e.target.value)}
                                        disabled={saving}
                                    />
                                </div>
                            </div>

                            {/* FixDesk triage type + "moved to requirements" toggle */}
                            <div className="nt-row">
                                <div className="nt-field">
                                    <div className="nt-label">
                                        FixDesk Type
                                        <span className="hint">FixDesk's own classification</span>
                                    </div>
                                    <select className="nt-select" value={fixdeskType} onChange={(e) => setFixdeskType(e.target.value)} disabled={saving}>
                                        <option value="">— Not set —</option>
                                        {FIXDESK_TYPE_VALUES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div className="nt-field">
                                    <div className="nt-label">
                                        Moved to Client Requirements
                                        <span className="hint">FixDesk-only flag</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={`nt-toggle ${moved ? "on" : ""}`}
                                        role="switch"
                                        aria-checked={moved}
                                        onClick={() => setMoved((v) => !v)}
                                        disabled={saving}
                                    >
                                        <span className="nt-toggle-track"><span className="nt-toggle-thumb" /></span>
                                        <span className="nt-toggle-label">{moved ? "Yes" : "No"}</span>
                                    </button>
                                </div>
                            </div>

                            {/* Hot-fix applied + Moved-to-sprint toggles */}
                            <div className="nt-row">
                                <div className="nt-field">
                                    <div className="nt-label">
                                        Hot-fix Applied
                                        <span className="hint">FixDesk-only flag</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={`nt-toggle ${hotfix ? "on" : ""}`}
                                        role="switch"
                                        aria-checked={hotfix}
                                        onClick={() => setHotfix((v) => !v)}
                                        disabled={saving}
                                    >
                                        <span className="nt-toggle-track"><span className="nt-toggle-thumb" /></span>
                                        <span className="nt-toggle-label">{hotfix ? "Yes" : "No"}</span>
                                    </button>
                                </div>
                                <div className="nt-field">
                                    <div className="nt-label">
                                        Moved to Sprint
                                        <span className="hint">FixDesk-only flag</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={`nt-toggle ${sprint ? "on" : ""}`}
                                        role="switch"
                                        aria-checked={sprint}
                                        onClick={() => setSprint((v) => !v)}
                                        disabled={saving}
                                    >
                                        <span className="nt-toggle-track"><span className="nt-toggle-thumb" /></span>
                                        <span className="nt-toggle-label">{sprint ? "Yes" : "No"}</span>
                                    </button>
                                </div>
                            </div>

                            {/* Internal note */}
                            <div className="nt-field">
                                <div className="nt-label">
                                    Internal Note
                                    <span className="hint">visible only inside FixDesk</span>
                                </div>
                                <textarea
                                    className="nt-textarea"
                                    placeholder="Triage notes for the support team…"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    disabled={saving}
                                />
                            </div>

                            {/* Ticket-level attachments — files uploaded
                                with the original ticket (comment === null). */}
                            {(() => {
                                const ticketAttachments = attachments.filter((a) => !a.comment);
                                if (ticketAttachments.length === 0) return null;
                                return (
                                    <div className="nt-field">
                                        <div className="nt-label">
                                            Attachments
                                            <span className="hint">{ticketAttachments.length} file{ticketAttachments.length === 1 ? "" : "s"} · uploaded with ticket</span>
                                        </div>
                                        <div style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                                            gap: 10,
                                        }}>
                                            {ticketAttachments.map((a) => <AttachmentTile key={a.id} a={a} />)}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* ── RIGHT pane: sticky activity timeline + reply ── */}
                        <aside className="nt-detail-right">
                            <div className="nt-field">
                                <div className="nt-label">
                                    Activity Timeline
                                    <span className="hint">
                                        {comments.length} message{comments.length === 1 ? "" : "s"}
                                        {attachments.length > 0 ? ` · ${attachments.length} attachment${attachments.length === 1 ? "" : "s"}` : ""}
                                        {detailLoading ? " · loading…" : ""}
                                    </span>
                                </div>

                                {ticket.assigned_to_name && (
                                    <div className="nt-assignee-chip">
                                        <div className="agent-avatar" style={{
                                            width: 18, height: 18, fontSize: 8.5,
                                            background: avatarGradient(ticket.assigned_to || ticket.assigned_to_name),
                                        }}>{initials(ticket.assigned_to_name)}</div>
                                        Currently assigned to {ticket.assigned_to_name}
                                    </div>
                                )}

                                <div ref={timelineRef} className="nt-timeline">
                                    <div className="nt-timeline-thread" />

                                    <TimelineEvent
                                        kind="system"
                                        actor={detail?.submitted_by_name || ticket.submitted_by_name || "User"}
                                        at={ticket.source_created_at}
                                        label="created this ticket"
                                    />

                                    {comments.map((c) => (
                                        <TimelineEvent
                                            key={c.id}
                                            kind="comment"
                                            actor={c.author_name || (c.from_role === "agent" ? "Agent" : "Customer")}
                                            role={c.from_role}
                                            at={c.source_created_at}
                                            message={c.message}
                                            attachments={c.attachments || []}
                                        />
                                    ))}

                                    {!detailLoading && comments.length === 0 && (
                                        <div style={{
                                            padding: "8px 0 6px 38px",
                                            color: "var(--cs-text-dim)",
                                            fontSize: 11.5,
                                        }}>
                                            No replies yet. Be the first to respond.
                                        </div>
                                    )}
                                </div>

                                {/* Reply composer */}
                                <div className="nt-reply-box" style={{ marginTop: 10 }}>
                                    <div className="nt-reply-head">
                                        <span className="lbl">Reply as agent</span>
                                        <span className="sub">(visible in eMedHub)</span>
                                    </div>
                                    <textarea
                                        className="nt-textarea"
                                        data-reply-composer="1"
                                        placeholder="Type your reply to the customer…"
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                                                e.preventDefault();
                                                handlePostReply();
                                            }
                                        }}
                                        disabled={posting}
                                        style={{ minHeight: 72, marginBottom: 8 }}
                                    />
                                    <div className="nt-reply-foot">
                                        <span className="nt-reply-hint">
                                            <span className="nt-kbd">⌘</span>+<span className="nt-kbd">Enter</span> to send
                                        </span>
                                        <button
                                            className="nt-btn primary"
                                            style={{ marginLeft: "auto", padding: "7px 14px" }}
                                            onClick={handlePostReply}
                                            disabled={!replyText.trim() || posting}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="22" y1="2" x2="11" y2="13" />
                                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                            </svg>
                                            {posting ? "Sending…" : "Send Reply"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>

                {/* Footer */}
                <div className="nt-footer">
                    <div className="nt-shortcuts">
                        <span className="nt-kbd">Esc</span> to close
                        <span style={{ margin: "0 4px" }}>·</span>
                        <span className="nt-kbd">⌘</span>+<span className="nt-kbd">Enter</span> to save
                    </div>
                    <div className="nt-actions">
                        <button className="nt-btn cancel" onClick={handleClose} disabled={saving}>Cancel</button>
                        <button className="nt-btn primary" onClick={handleSave} disabled={!dirty || saving}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};

const MetaRow = ({ label, value }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={{
            fontSize: 10,
            color: "var(--cs-text-dim)",
            textTransform: "uppercase",
            letterSpacing: ".5px",
            fontWeight: 600,
        }}>{label}</span>
        <span style={{ fontSize: 12.5, color: "var(--cs-text)" }}>{value || "—"}</span>
    </div>
);

/* One row in the activity timeline. Two shapes:
   - kind="system"  → bubble-less event line ("X created this ticket", "Assigned to Y", …)
   - kind="comment" → avatar + chat bubble with author/role/time/message
*/
const TimelineEvent = ({ kind, actor, role, at, label, message, attachments }) => {
    if (kind === "system") {
        return (
            <div style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "8px 0", position: "relative",
            }}>
                <div style={{
                    width: 22, height: 22, borderRadius: "50%",
                    background: "var(--cs-surface-2)",
                    border: "1px solid var(--cs-border-hi)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, zIndex: 1,
                    color: "var(--cs-text-dim)",
                }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--cs-text-sec)" }}>
                    <strong style={{ color: "var(--cs-text)" }}>{actor}</strong> {label}
                </div>
                <span style={{ fontSize: 10.5, color: "var(--cs-text-dim)", fontFamily: "var(--cs-mono)" }}>
                    {relativeTime(at)}
                </span>
            </div>
        );
    }

    const isAgent = role === "agent";
    return (
        <div style={{ display: "flex", gap: 12, padding: "10px 0", position: "relative" }}>
            <div className="agent-avatar" style={{
                width: 22, height: 22, fontSize: 9,
                background: avatarGradient(actor),
                flexShrink: 0, zIndex: 1,
            }}>
                {initials(actor)}
            </div>
            <div style={{
                flex: 1, minWidth: 0,
                background: "var(--cs-surface)",
                border: "1px solid var(--cs-border)",
                borderRadius: 8,
                padding: "8px 10px",
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-text)" }}>{actor}</span>
                    <span style={{
                        fontSize: 9.5,
                        textTransform: "uppercase",
                        letterSpacing: ".4px",
                        color: isAgent ? "var(--cs-accent-l)" : "var(--cs-text-dim)",
                        background: isAgent ? "var(--cs-accent-soft)" : "var(--cs-surface-2)",
                        padding: "1px 6px",
                        borderRadius: 4,
                        fontWeight: 600,
                    }}>{isAgent ? "Agent" : "User"}</span>
                    <span style={{ fontSize: 10.5, color: "var(--cs-text-dim)", marginLeft: "auto", fontFamily: "var(--cs-mono)" }}>
                        {relativeTime(at)}
                    </span>
                </div>
                <div style={{ fontSize: 12.5, color: "var(--cs-text-sec)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                    {message || <span style={{ color: "var(--cs-text-dim)" }}>(no message)</span>}
                </div>
                {attachments && attachments.length > 0 && (
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                        gap: 6,
                        marginTop: 8,
                    }}>
                        {attachments.map((a) => <AttachmentTile key={a.id} a={a} compact />)}
                    </div>
                )}
            </div>
        </div>
    );
};


/* Single attachment tile — image preview for image MIME types, file-icon
   row otherwise. Click anywhere on the tile opens the file in a new tab. */
const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentTile = ({ a, compact }) => {
    const url = a?.url || "";
    const hasUrl = !!url && (url.startsWith("http://") || url.startsWith("https://"));
    const isImage = a?.is_image || /^image\//i.test(a?.mime_type || "");
    const size = formatBytes(a?.size);

    const tile = (
        <div style={{
            background: "var(--cs-bg)",
            border: "1px solid var(--cs-border)",
            borderRadius: 8,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            cursor: hasUrl ? "pointer" : "default",
            transition: "border-color .15s",
        }}
             onMouseEnter={(e) => { if (hasUrl) e.currentTarget.style.borderColor = "var(--cs-accent)"; }}
             onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--cs-border)"; }}
        >
            {isImage && hasUrl ? (
                <img
                    src={url}
                    alt={a.name}
                    loading="lazy"
                    style={{
                        width: "100%",
                        height: compact ? 80 : 110,
                        objectFit: "cover",
                        background: "var(--cs-surface-2)",
                    }}
                />
            ) : (
                <div style={{
                    height: compact ? 80 : 110,
                    background: "var(--cs-surface-2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--cs-text-dim)",
                }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                    </svg>
                </div>
            )}
            <div style={{ padding: "6px 8px", minWidth: 0 }}>
                <div style={{
                    fontSize: compact ? 11 : 12,
                    color: "var(--cs-text)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: 500,
                }} title={a.name}>
                    {a.name || "file"}
                </div>
                <div style={{
                    fontSize: 10, color: "var(--cs-text-dim)",
                    fontFamily: "var(--cs-mono)", marginTop: 2,
                }}>
                    {size}{!hasUrl && " · not viewable"}
                </div>
            </div>
        </div>
    );

    if (!hasUrl) return tile;
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>
            {tile}
        </a>
    );
};


export default TicketDetailModal;
